import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@readcloud/ui';
import { Button } from '@readcloud/component-library';
import { PageLabelList } from '@readcloud/data';
import { AnnotationsState } from '@readcloud/state';
import C from 'classnames';
import moize from 'moize';
import React, { useRef } from 'react';
import { ToolbarContainer } from '../ToolbarContainers/ToolbarContainer';
import { PageNavigation } from './PageNavigation';
import { PageScalerComponent } from './PageScaler';

import { MdSettings, MdToc } from 'react-icons/md';
import { ConnectedEPUBSettings } from '../../Modals/ConnectedEPUBSettings';
import { ConnectedPDFSettings } from '../../Modals/ConnectedPDFSettings';
import { trackEvent } from '@readcloud/common';
import { Switch } from '@readcloud/ui';

const getLabelsFromNumPages = moize((numPages) =>
  Array.from(Array(numPages), (_, i) => ({
    value: `${i + 1}`,
    label: `${i + 1}`,
  }))
);
interface Props {
  className: string;
  isOpen?: boolean;
  documentType?: string;
  pageChangeButtonsClassName: string;
  pageSeekClassName: string;
  nextLocation: () => void;
  previousLocation: () => void;
  onLocationChange: (next: string) => void;
  onPageViewModeChange?: (mode: string) => void;
  pageViewMode: string;
  pageIndex: number;
  numLocations: number;
  scale?: number;
  onScaleChange?: (scale: number) => void;
  onIncreaseScale?: () => void;
  onDecreaseScale?: () => void;
  noPageViewMode?: boolean;
  noSlidesViewSwitch?: boolean;
  onWalkthrough?: boolean;
  pageLabels?: PageLabelList;
  onToc?: () => void;
  onToggleSlidesView?: () => void;
  slidesView?: boolean;
  loadingPageLabels?: boolean;
  SettingsComponent?:
    | typeof ConnectedEPUBSettings
    | typeof ConnectedPDFSettings;
  currentlyDrawing?: AnnotationsState['currentlyDrawing'];
  onDiscardDrawing: () => void;
  outerWidth?: number;
  initialPageWidth?: number;
  customTextSelection?: boolean;
}

const sideComponentSize = 300;
const Component = ({
  isOpen,
  documentType,
  nextLocation,
  previousLocation,
  onLocationChange,
  onPageViewModeChange,
  pageViewMode,
  pageLabels,
  pageIndex,
  numLocations,
  scale,
  onScaleChange,
  onIncreaseScale,
  onDecreaseScale,
  noPageViewMode,
  onToc,
  onWalkthrough,
  loadingPageLabels,
  SettingsComponent,
  pageChangeButtonsClassName,
  pageSeekClassName,
  className,
  currentlyDrawing,
  onDiscardDrawing,
  outerWidth,
  initialPageWidth,
  customTextSelection,
}: Props) => {
  const tocButtonRef = useRef<HTMLButtonElement>(null);
  const settingsButtonRef = useRef<HTMLButtonElement>(null);

  const [showTocTooltip, setShowTocTooltip] = React.useState<boolean>(false);

  return (
    <ToolbarContainer
      className={C(
        className,
        customTextSelection ? 'iosTextSelectNone textSelectNone' : ''
      )}
      isOpen={isOpen && !currentlyDrawing}
    >
      <div className="rc-flex rc-container-fluid rc-justify-between rc-items-center rc-pb-2 rc-pt-3">
        <div className="rc-flex rc-justify-start rc-items-center rc-w-full rc-max-md:rc-w-auto">
          <React.Fragment>
            {SettingsComponent ? (
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    icon
                    variant="light"
                    onClick={() => {
                      trackEvent('Webapp/BottomToolbarSettingsClicked', {});
                    }}
                    ref={settingsButtonRef}
                  >
                    <MdSettings size="28" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="rc-z-[1050] rc-rounded-md max-sm:rc-w-[17rem]"
                  collisionPadding={16}
                  sideOffset={16}
                >
                  <SettingsComponent />
                </PopoverContent>
              </Popover>
            ) : null}
            <div className="rc-px-1" />
            {onScaleChange ? (
              <div className="rc-flex max-md:rc-hidden">
                {documentType === 'PDF' && (
                  <PageScalerComponent
                    value={scale || 1}
                    onIncrease={onIncreaseScale}
                    onDecrease={onDecreaseScale}
                    onChange={onScaleChange}
                    outerWidth={outerWidth || 0}
                    initialPageWidth={initialPageWidth || 0}
                    loading={loadingPageLabels || numLocations === 0}
                  />
                )}
                {!noPageViewMode ? (
                  <div className="rc-flex rc-flex-row rc-items-center">
                    <small className="rc-mr-2">Page View Mode</small>
                    <Switch
                      checked={pageViewMode === 'two'}
                      onCheckedChange={(checked) =>
                        onPageViewModeChange &&
                        onPageViewModeChange(checked ? 'two' : 'one')
                      }
                    />
                  </div>
                ) : null}
                <div className="rc-px-1" />
              </div>
            ) : null}
          </React.Fragment>
        </div>

        <div className="rc-flex rc-flex-2 rc-items-center rc-justify-center rc-w-full max-md:.rc-w-auto">
          <PageNavigation
            loading={loadingPageLabels || numLocations === 0}
            max={numLocations}
            next={nextLocation}
            previous={previousLocation}
            onChange={({ value, label }) => {
              onLocationChange(value);
            }}
            valueIndex={pageIndex - 1}
            pageLabels={pageLabels || getLabelsFromNumPages(numLocations)}
            pageChangeButtonsClassName={pageChangeButtonsClassName}
            pageSeekClassName={pageSeekClassName}
          />
        </div>
        <div
          className={`rc-flex rc-items-center rc-justify-end rc-w-full max-md:.rc-w-auto  `}
        >
          {onToc ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  icon
                  variant="light"
                  onClick={() => {
                    onToc();
                    setShowTocTooltip(false);
                  }}
                  ref={tocButtonRef}
                >
                  <MdToc size="28" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Table of Contents</TooltipContent>
            </Tooltip>
          ) : (
            <div></div>
          )}
        </div>
      </div>
    </ToolbarContainer>
  );
};
export const BottomToolbar = Component;
